import { inject } from '@angular/core';
import { patchState, signalStore, withMethods, withState } from '@ngrx/signals';
import { ProductDTO, ProductOrderDTO, ProductService, PaginatedProductDTO } from '@api';
import { lastValueFrom } from 'rxjs';

interface ProductState {
  products: ProductDTO[];
  product: ProductDTO | null;
  isLoading: boolean;
  error: boolean;
}

const initialState: ProductState = {
  products: [],
  product: null,
  isLoading: false,
  error: false,
};

export const ProductStore = signalStore(
  { providedIn: 'root' },
  withState(initialState),

  withMethods((store) => {
    const productService = inject(ProductService);

    return {
      fetchProducts() {
        patchState(store, () => ({
          isLoading: true,
          error: false,
        }));

        productService.getProducts().subscribe({
          next: (paginatedProducts: PaginatedProductDTO) => {
            patchState(store, () => ({
              products: paginatedProducts?.elements || [],
              isLoading: false,
            }));
          },
          error: (error) => {
            console.error(`Error while getting all products: ${error}`);
            patchState(store, () => ({
              isLoading: false,
              error: true,
            }));
          },
        });
      },

      async fetchProductById(productId: string) {
        patchState(store, () => ({
          isLoading: true,
          error: false,
        }));
        try {
          const product = await lastValueFrom(productService.getProductsById(productId));
          patchState(store, () => ({
            product: product,
            isLoading: false,
          }));
        } catch (e) {
          console.error(`Error while getting product: ${e}`);
          patchState(store, () => ({
            isLoading: false,
            error: true,
          }));
        }
      },

      createProducts(productOrder: ProductOrderDTO) {
        patchState(store, () => ({
          isLoading: true,
          error: false,
        }));

        productService.createProducts(productOrder).subscribe({
          next: () => {
            this.fetchProducts();
          },
          error: (error) => {
            console.error(`Error while creating product: ${error}`);
            patchState(store, () => ({
              isLoading: false,
              error: true,
            }));
          },
        });
      },

      deleteProducts(product: ProductDTO) {
        patchState(store, () => ({
          isLoading: true,
          error: false,
        }));

        productService.deleteProducts(product.id).subscribe({
          next: () => {
            this.fetchProducts();
          },
          error: (error) => {
            console.error(`Error while deleting product: ${error}`);
            patchState(store, {
              isLoading: false,
              error: true,
            });
          },
        });
      },
    };
  }),
);
